import { LazyQueryExecFunction, QueryResult } from "@apollo/client/react/types/types";
import { Css, Filters, multiFilter, toggleFilter } from "@homebound/beam";
import { capitalCase } from "change-case";
import { useState } from "react";
import { SearchBox } from "src/components";
import { DisplayNamedFragment } from "src/fragments/commonFragments.generated";
import { CommitmentStatus } from "src/generated/graphqlTypes";
import { useProjectContext } from "src/routes/projects/context/ProjectContext";
import { ProjectLayout_ProjectFragment } from "src/routes/projects/ProjectLayout.generated";
import { queryResult } from "src/utils";
import { CommittedAtStartPerProjectTable } from "./CommittedAtStartPerProjectTable";
import {
  CommittedAtStartTab_CommitmentsByProjectCostCodeQuery,
  CommittedAtStartTab_CommitmentsByProjectCostCodeQueryVariables,
  CommittedAtStartTab_ProjectCostCodeFragment,
  CommittedAtStartTab_ProjectFragment,
  CommittedAtStartTabQueryVariables,
  useCommittedAtStartTab_CommitmentsByProjectCostCodeLazyQuery,
  useCommittedAtStartTabQuery,
} from "./CommittedAtStartTab.generated";

type FilterOptions = {
  project?: string[];
  costCode?: string[];
  commitmentStatus?: CommitmentStatus[];
  showReleased: boolean;
};

export type CommittedAtStartTabMappedProjectCostCode = {
  project: CommittedAtStartTab_ProjectFragment;
  projectCostCodes: CommittedAtStartTab_ProjectCostCodeFragment[];
};

export function CommittedAtStartTab() {
  const { sellableChildren } = useProjectContext();
  const [search, setSearch] = useState<string | undefined>();
  const [filter, setFilter] = useState<FilterOptions>({ showReleased: false });
  const query = useCommittedAtStartTabQuery({ variables: mapToFilter(filter, sellableChildren) });
  const [fetchProjectCostCodeData, projectCostCodeData] = useCommittedAtStartTab_CommitmentsByProjectCostCodeLazyQuery({
    fetchPolicy: "no-cache",
  });

  return queryResult(query, (data) => {
    // Group the data by project and map to a new structure easier to work with in the table
    const groupedData = data.projectCostCodes.entities
      .groupBy((pcc) => pcc.project.id)
      .toValues()
      .map((pccs) => ({
        project: pccs.first?.project,
        projectCostCodes: pccs,
      })) as CommittedAtStartTabMappedProjectCostCode[];
    return (
      <div>
        <div css={Css.df.mb2.$}>
          <div css={Css.df.cg1.$}>
            <SearchBox onSearch={setSearch} />
            <div>
              <Filters
                filterDefs={getFilterDefs(
                  sellableChildren,
                  data.projectCostCodes.entities.map((pcc) => pcc.costCode).unique(),
                )}
                filter={filter}
                onChange={setFilter}
              />
            </div>
          </div>
        </div>
        <CommittedAtStartPerProjectTable
          data={groupedData}
          projectCostCodeData={projectCostCodeData}
          fetchProjectCostCodeData={fetchProjectCostCodeData}
          search={search}
          commitmentStatusFilter={filter.commitmentStatus}
          showReleased={filter.showReleased}
        />
      </div>
    );
  });
}

export type CommittedAtStartTab_ProjectCostCodeDataFetch = LazyQueryExecFunction<
  CommittedAtStartTab_CommitmentsByProjectCostCodeQuery,
  CommittedAtStartTab_CommitmentsByProjectCostCodeQueryVariables
>;

export type CommittedAtStartTab_ProjectCostCodeResponse = QueryResult<
  CommittedAtStartTab_CommitmentsByProjectCostCodeQuery,
  CommittedAtStartTab_CommitmentsByProjectCostCodeQueryVariables
>;

function getFilterDefs(childrenSellable: ProjectLayout_ProjectFragment[], costCodes: DisplayNamedFragment[]) {
  return {
    project: multiFilter({
      options: childrenSellable.sortByKey("name"),
      getOptionValue: (p) => p.id,
      getOptionLabel: (p) => p.name,
      label: "Project",
    }),
    costCode: multiFilter({
      options: costCodes,
      getOptionValue: (c) => c.id,
      getOptionLabel: (c) => c.displayName,
      label: "Cost Code",
    }),
    commitmentStatus: multiFilter({
      options: Object.values(CommitmentStatus).map((cs) => ({ label: cs, value: cs })),
      getOptionLabel: (cs) => capitalCase(cs.label),
      getOptionValue: (cs) => cs.value,
      label: "PO Status",
    }),
    showReleased: toggleFilter({ label: "Show Released" }),
  };
}

function mapToFilter(
  filter: FilterOptions,
  childrenSellable: ProjectLayout_ProjectFragment[],
): CommittedAtStartTabQueryVariables {
  return {
    project: filter.project?.nonEmpty ? filter.project : childrenSellable.map((p) => p.id),
    costCode: filter.costCode?.nonEmpty ? filter.costCode : undefined,
  };
}
